import type { EventAction, MemberId, Ticket, TicketCommand, TicketDraft, TicketEvent, TicketState } from './types';
import { validateDraft } from './validation';

interface Transition {
  action: EventAction;
  state: TicketState;
  note?: string;
}

export function createTicket(
  draft: TicketDraft,
  coupleId: string,
  creatorId: MemberId,
  at: string,
  eventId: string,
): Ticket {
  const validationErrors = validateDraft(draft);
  const firstError = Object.values(validationErrors)[0];
  if (firstError) throw new Error(firstError);

  const recipientId: MemberId = creatorId === 'creator' ? 'partner' : 'creator';
  const event: TicketEvent = {
    id: eventId,
    parentIds: [],
    actor: creatorId,
    action: 'created',
    at,
    state: 'pending_response',
  };

  return {
    ...draft,
    id: crypto.randomUUID(),
    coupleId,
    creatorId,
    recipientId,
    createdAt: at,
    updatedAt: at,
    state: 'pending_response',
    headEventId: eventId,
    events: [event],
  };
}

export function applyCommand(ticket: Ticket, command: TicketCommand, at: string, eventId: string): Ticket {
  const transition = allowedTransition(ticket, command);
  const event: TicketEvent = {
    id: eventId,
    parentIds: [ticket.headEventId],
    actor: command.actor,
    action: transition.action,
    at,
    note: transition.note,
    state: transition.state,
  };

  return {
    ...ticket,
    state: transition.state,
    updatedAt: at,
    headEventId: eventId,
    events: [...ticket.events, event],
  };
}

function allowedTransition(ticket: Ticket, command: TicketCommand): Transition {
  if (ticket.state === 'rejected' || ticket.state === 'completed') {
    throw new Error('已结束的单据不能再改变状态');
  }

  switch (command.action) {
    case 'accept':
      if (ticket.state !== 'pending_response') throw new Error('当前状态不允许接受');
      if (command.actor !== ticket.recipientId) throw new Error('只有接单方可以接受');
      return { action: 'accepted', state: 'accepted' };
    case 'reject':
      if (ticket.state !== 'pending_response') throw new Error('当前状态不允许拒绝');
      if (command.actor !== ticket.recipientId) throw new Error('只有接单方可以拒绝');
      validateReply(command.note, '拒绝原因');
      return { action: 'rejected', state: 'rejected', note: command.note.trim() };
    case 'submit':
      if (ticket.state !== 'accepted') throw new Error('当前状态不允许提交');
      if (command.actor !== ticket.recipientId) throw new Error('只有接单方可以提交');
      if (command.note !== undefined) validateOptionalReply(command.note);
      return { action: 'submitted', state: 'pending_confirmation', note: command.note?.trim() };
    case 'confirm':
      if (ticket.state !== 'pending_confirmation') throw new Error('当前状态不允许确认');
      if (command.actor !== ticket.creatorId) throw new Error('只有开单方可以确认');
      return { action: 'confirmed', state: 'completed' };
  }
}

function validateReply(note: string, label: string): void {
  if (!note.trim()) throw new Error(`请填写${label}`);
  if (countCharacters(note.trim()) > 120) throw new Error(`${label}最多 120 个字`);
}

function validateOptionalReply(note: string): void {
  if (countCharacters(note.trim()) > 120) throw new Error('回应最多 120 个字');
}

function countCharacters(value: string): number {
  return [...value].length;
}
